import type { PublicContent } from './fr'

const todo = '[To be completed]'

export const en: PublicContent = {
  common: {
    backHome: '← Back to home',
    updatedPrefix: 'Last updated:',
    updatedAt: 'October 10, 2026',
    draftBanner: 'Provisional text, being drafted: passages in square brackets will be completed.',
    todo,
    footerRights: '© 2026 Boutiklic kaatomi-laafou. All rights reserved.',
    footerNavAria: 'Legal pages',
    footerBilling: 'Invoicing compliance',
    footerPrivacy: 'Privacy policy',
    footerTerms: 'Terms of use (Terms of Sale)',
    footerNotice: 'Legal notice',
    cities: 'Conakry (Guinea)',
    whatsappMessage: 'Hello, I have a question about Boutiklic.',
    hours: [{ label: 'Monday – Friday', value: '8 am – 6 pm' }],
    contactSupportLegal: 'Contact legal support',
  },

  notice: {
    docTitle: 'Legal notice',
    intro: 'Legal information about the publisher and the host of the Boutiklic service.',
    eyebrow: 'Legal & juridical information',
    tocTitle: 'Contents & quick navigation',
    tocAria: 'Legal sections contents',
    toc: ['Service publisher', 'Hosting', 'Intellectual property', 'Personal data', 'Liability & Links', 'Contact & Jurisdiction'],
    editorTitle: 'Service publisher',
    companyLabel: 'Company name',
    companyValue: `Boutiklic Technologies ${todo}`,
    formLabel: 'Legal form and share capital',
    formValue: '[SAS with a share capital of 10,000,000 GNF - To be completed]',
    seatLabel: 'Registered office',
    regLabel: 'Registration & legal register',
    directorLabel: 'Publication director',
    directorValue: '[Boutiklic general management - To be completed]',
    contactLabel: 'Official contact',
    hostingTitle: 'Hosting',
    hostingBefore: 'The service and the data of the ',
    hostingAfter: ' platform are hosted on highly secure cloud infrastructure that complies with international standards:',
    hostingVisit: 'Visit the host',
    ipTitle: 'Intellectual property',
    ip1: 'All the elements making up the Boutiklic service (including the brand, logos, texts, graphical interfaces, source code, databases and software documentation) are protected by national and international intellectual property and copyright laws.',
    ip2Before: 'Any reproduction, representation, modification, distribution or total or partial use of the elements of the site and of the SaaS application, without the prior express written consent of ',
    ip2After: ', is strictly prohibited and may constitute an infringement.',
    ipNote: 'Third-party trademarks, trade names and logos appearing on the platform remain the exclusive property of their respective owners.',
    dataTitle: 'Personal data & Privacy',
    data1: 'Boutiklic attaches great importance to the confidentiality and security of the data of merchants and their customers. Data processing carried out in operating the platform follows the principles of transparency, proportionality and security in line with regulatory best practices.',
    data2: 'To learn more about how your data is collected, to exercise your rights of access, rectification or deletion, or to contact our data protection officer:',
    dataButton: 'Read the privacy policy',
    liabilityTitle: 'Limitation of liability',
    liability1: 'Boutiklic makes every effort to ensure the accuracy of the information and the continuous operation of its services. However, Boutiklic cannot be held liable for temporary interruptions for maintenance, unintentional errors or the unsuitability of its service for the specific needs of a particular user.',
    liability2: 'Hyperlinks that may appear on the platform pointing to external sites do not engage the liability of Boutiklic as to their content or their data management practices.',
    ctaTag: 'Regulatory support',
    ctaTitle: 'A legal or regulatory question?',
    ctaText: 'Our legal and compliance team is available to answer your questions about merchant contracts, data protection or partnerships.',
    ctaHint: 'Reply guaranteed within 24 to 48 business hours',
  },

  terms: {
    docTitle: 'Terms of Sale',
    intro: 'Terms applicable to the subscription and use of the Boutiklic service.',
    eyebrow: 'Legal & contractual terms',
    draftTitle: 'Document being finalised by legal',
    draftText: 'Provisional text, being drafted: passages in square brackets will be completed shortly.',
    tocTitle: 'Quick navigation & contents',
    tocAria: 'Interactive contents',
    toc: ['Purpose', 'Subscription & Pricing', 'Termination', 'Liability', 'Law & Disputes'],
    s1Title: 'Purpose',
    s1Lead: 'These terms govern access to and use of the Boutiklic service (point of sale, stock management, invoicing).',
    modules: [
      { title: 'Touch point of sale', text: 'Smooth point of sale suited to selling in a physical shop.' },
      { title: 'Stock management', text: 'Dynamic inventory, alert thresholds and multi-warehouse management.' },
      { title: 'Invoicing', text: 'Quotes, compliant tickets, WhatsApp receipts and accounting follow-up.' },
    ],
    s2Title: 'Subscription and pricing',
    s2Lead: 'Offers, prices and payment terms:',
    recapTitle: 'Terms planned in the contract',
    recap: [
      { label: 'Subscription plans', value: 'Monthly or Annual', text: 'Billed in advance on the account anniversary date.' },
      { label: 'Supported currencies', value: 'GNF / FCFA / EUR', text: 'Transparent pricing according to your country of operation.' },
      { label: 'Accepted payments', value: 'Mobile Money & Cards (coming soon)', text: 'Orange Money, MTN, Wave, Visa/Mastercard bank cards.' },
    ],
    s3Title: 'Termination',
    s3Lead: 'Termination conditions and notice periods:',
    s3Bullets: [
      '• The monthly subscription, with no commitment, can be stopped at any time from the customer account administration area.',
      '• On termination, the customer has 30 days to freely export their data catalogues, sales journals and accounting entries in CSV / Excel format.',
    ],
    s4Title: 'Liability',
    s4Lead: 'Limits of the publisher’s liability and obligations of the user:',
    dutyEditorTitle: 'Publisher’s obligations',
    dutyEditorText: 'Reasonable efforts to guarantee 24/7 availability of the software infrastructure, automatic encrypted daily backups.',
    dutyUserTitle: 'User’s obligations',
    dutyUserText: 'Strict confidentiality of administrator and cashier access credentials, compliance with applicable commercial and tax legislation.',
    s5Title: 'Governing law and disputes',
    s5Lead: 'Governing law and competent jurisdiction:',
    s5P1: 'In the event of a dispute relating to the performance or interpretation of these terms, the parties undertake to favour an amicable settlement within thirty (30) business days.',
    s5P2: 'Failing an amicable agreement, the competent courts of the jurisdiction of Conakry will have exclusive jurisdiction.',
    ctaTag: 'Contract support',
    ctaTitle: 'A question about our terms of sale?',
    ctaText: 'Our legal and sales team is here to clarify the subscription terms and your contractual commitments.',
    ctaButton: 'Contact the sales team',
  },

  privacy: {
    docTitle: 'Merchant data protection',
    intro: 'How Boutiklic collects, uses and protects the data of your shop and your customers.',
    eyebrow: 'Data protection & Privacy',
    tocTitle: 'Interactive contents',
    tocAria: 'Interactive contents',
    toc: ['Data collected', 'Purposes', 'Processors', 'Retention', 'Your rights'],
    s1Title: 'Data collected',
    s1Lead: 'Account data (name, phone number or e-mail), shop data (products, stock, sales, quotes, invoices) and data about your customers that you enter in the application.',
    kinds: [
      { title: 'Account data', text: 'Business name, manager’s mobile phone and login e-mail.' },
      { title: 'Shop data', text: 'Product catalogue, stock, point-of-sale sales, quotes & invoicing.' },
      { title: 'Your customer file', text: 'Customer contact details and purchase history entered manually.' },
    ],
    s2Title: 'Purposes',
    s2Lead: 'Providing the service (point of sale, stock, invoicing), securing access (verification code by SMS) and keeping your devices in sync.',
    purposes: [
      { title: 'Running the service', text: 'Running your point of sale: fast checkout, inventory, restocking and issuing tax documents.' },
      { title: 'Access security', text: 'Verification by one-time code (OTP) sent by SMS or e-mail to protect your till from intrusion.' },
      { title: 'Synchronisation', text: 'Instant, wireless update of your tickets, stock and prices between your terminal, tablet and mobile.' },
    ],
    s3Title: 'Processors',
    s3Lead: `Hosting: OVH. SMS sending: messaging providers. File storage: ${todo}.`,
    subHostTitle: 'Main cloud hosting',
    subHostText: 'OVH Group SAS — certified data centres located in France / EU',
    subHostTag: 'GDPR compliant',
    subSmsTitle: 'Transactional SMS sending',
    subSmsText: 'Providers specialised in delivering OTP codes & alerts',
    subSmsTag: 'Approved messaging',
    subFilesTitle: 'File storage',
    subFilesTag: 'Being specified',
    s4Title: 'Retention',
    s4Lead: 'Data retention period:',
    boxTitle: 'Contractual details being drafted:',
    boxItems: [
      'Active retention guaranteed throughout the legal term of the Boutiklic subscription.',
      'Backup of accounting records and invoices in accordance with legal tax obligations.',
      'Reversibility period and full export of merchant data on termination.',
    ],
    s5Title: 'Your rights',
    s5Lead: 'You can request access to, rectification or deletion of your data by writing to:',
    rights: [
      { title: 'Right of access', text: 'View all of your data' },
      { title: 'Rectification', text: 'Update inaccurate information' },
      { title: 'Erasure', text: 'Right to permanent deletion' },
      { title: 'Portability', text: 'Export in standard formats (CSV, JSON)' },
    ],
    channelLabel: 'Channel dedicated to GDPR / Privacy requests:',
    channelButton: 'Write to the data officer',
    ctaTag: 'Support & Compliance',
    ctaTitle: 'A question about how your data is managed?',
    ctaText: 'Our team dedicated to compliance and data protection supports you and answers all your questions transparently.',
    ctaButton: 'Exercise my rights / Contact the DPO',
  },

  billing: {
    docTitle: 'Invoicing compliance',
    intro: 'Information on the compliance of the invoicing documents issued with Boutiklic.',
    eyebrow: 'Compliance & Tax regulation',
    tocTitle: 'Document contents',
    tocAria: 'Document contents',
    toc: ['Documents issued', 'Approval'],
    s1Title: 'Documents issued',
    s1Lead: 'Boutiklic lets you issue quotes, tickets and invoices. The mandatory mentions depend on the regulations of your country.',
    docs: [
      { title: 'Quotes', text: 'Itemised proforma quotes, a clear estimate before the sale with a configurable validity period.' },
      { title: 'Tickets', text: 'Instant till tickets, compact thermal receipts or digital ones (SMS / WhatsApp).' },
      { title: 'Invoices', text: 'Compliant official invoices, sequential numbering, VAT/taxes and tamper-proof traceability.' },
    ],
    localTitle: 'Adapting to local regulations',
    localText: 'The Boutiklic interface includes tax identification fields (NIF, RCCM, VAT registration key) and lets you adjust tax rates according to the country of operation (Guinea, Senegal, WAEMU/ECOWAS zone, France and international).',
    s2Title: 'Approval',
    s2Before: 'Official approval status of the invoicing solution:',
    s2After: '. Do not publish any approval mention without supporting evidence.',
    statusLabel: 'Official status',
    statusTag: 'Under qualification',
    statusTitle: 'Certification and administrative approval',
    statusText: 'Ministerial or tax approval references are only displayed once the formal certificates and compliance receipts have been issued.',
    transparency: 'Boutiklic transparency commitment: no false mention is attached to documents before legal validation.',
    ctaTag: 'Regulatory & Tax Support',
    ctaTitle: 'A question about the compliance of your invoices?',
    ctaText: 'Our support team helps you configure your mandatory mentions and adapt your documents to your local legislation.',
    ctaButton: 'Contact the compliance team',
  },

  contact: {
    docTitle: 'Contact',
    badge: 'Contact & Support',
    h1: 'Let’s talk about your shop.',
    introBefore: 'A question, a demo, a technical problem: our team replies in ',
    langs: ['French', 'English', 'Portuguese'],
    introSep1: ', ',
    introSep2: ' and ',
    addressTitle: 'Address',
    phoneTitle: 'Phone',
    phoneBadge: 'Direct line',
    whatsappTitle: 'WhatsApp',
    whatsappBadge: 'Online',
    whatsappButton: 'Write on WhatsApp',
    hoursTitle: 'Opening hours',
    asideAria: 'Direct support channels',
    formAria: 'Contact form',
    fullName: 'Full name',
    fullNamePh: 'Your name',
    shopName: 'Shop name',
    shopNamePh: 'Mariama’s Shop',
    email: 'E-mail',
    emailPh: 'you@example.com',
    phone: 'Phone',
    phonePh: '+224 620 00 00 00',
    subject: 'Subject',
    topics: {
      DEMO: 'Demo request',
      SUPPORT: 'Assistance & Technical support',
      PRICING: 'Questions about subscriptions & pricing',
      PARTNERSHIP: 'Partnership & Distribution',
      OTHER: 'Other request',
    },
    message: 'Message',
    messagePh: 'Describe what you need...',
    minChars: 'At least 10 characters.',
    attachment: 'Attachment',
    optional: '(optional)',
    dropLink: 'Upload a document',
    dropOr: 'or drag and drop',
    dropHint: 'PNG, JPG, PDF · up to 3 files, 5 MB each',
    remove: 'Remove',
    honeypot: 'Do not fill in',
    send: 'Send message',
    sending: 'Sending…',
    privacyNote: 'Your information is confidential and secure. No unwanted messages.',
    successTitle: 'Message sent.',
    successText: 'Thank you! We will get back to you as soon as possible by e-mail or phone.',
    tooMany: 'You have sent too many messages. Try again in an hour or write to us on WhatsApp.',
    fileType: '“{name}”: unsupported format (JPEG, PNG, WebP images or PDF).',
    fileTooBig: '“{name}” exceeds 5 MB.',
    fileMax: 'Up to 3 attachments.',
    unitKb: 'KB',
    unitMb: 'MB',
  },
}
